/**
 * Identifiers and seeds derived from a sound's slug.
 *
 * Nothing in the library may call `crypto.randomUUID()` or an unseeded random: asking for
 * the same recipe twice has to give two *equal* patches and byte-identical audio, or a
 * preview and its bounce stop matching and every diff thinks the library changed.
 *
 * The arithmetic is 64-bit and therefore `bigint` rather than `number` — these are the same
 * values `LibraryIdentity` computes on iOS, so a sound rendered in the browser and the same
 * sound rendered on the phone share a seed and produce the same noise sample for sample.
 */

import type { UUID } from '../../model/types';

const MASK = 0xffff_ffff_ffff_ffffn;
const FNV_OFFSET = 0xcbf2_9ce4_8422_2325n;
const FNV_PRIME = 0x0000_0100_0000_01b3n;
/** The golden-ratio odd constant SplitMix64 uses; here it spaces one slug's slots apart. */
const GOLDEN_GAMMA = 0x9e37_79b9_7f4a_7c15n;

const utf8 = new TextEncoder();

/**
 * FNV-1a. Small, stable across platforms and releases — unlike a runtime string hash, which
 * is salted per process and would give a different library every launch.
 */
export function libraryHash(text: string): bigint {
  let value = FNV_OFFSET;
  for (const byte of utf8.encode(text)) {
    value ^= BigInt(byte);
    value = (value * FNV_PRIME) & MASK;
  }
  return value;
}

/**
 * Per-layer seed. Two noise layers in the same sound must not render the identical noise —
 * stacked, identical noise is one louder burst, not two hands clapping.
 */
export function librarySeed(slug: string, slot: number): bigint {
  const value = (libraryHash(slug) + BigInt(slot + 1) * GOLDEN_GAMMA) & MASK;
  return value === 0n ? 1n : value;
}

function hex(byte: number): string {
  return byte.toString(16).padStart(2, '0').toUpperCase();
}

/**
 * "PADLABLIB" then the scope, the slot, and four bytes of the slug's hash. Recognisable in a
 * JSON dump and impossible to collide with a real random UUID.
 */
export function libraryUUID(scope: number, slug: string, slot: number): UUID {
  const digest = libraryHash(slug);
  const at = (shift: bigint): number => Number((digest >> shift) & 0xffn);
  const bytes = [
    0x50, 0x41, 0x44, 0x4c, 0x41, 0x42, 0x4c, 0x49, 0x42,
    scope & 0xff,
    slot & 0xff,
    at(24n),
    at(16n),
    at(8n),
    at(0n),
    at(32n),
  ];
  const text = bytes.map(hex).join('');
  return `${text.slice(0, 8)}-${text.slice(8, 12)}-${text.slice(12, 16)}-${text.slice(16, 20)}-${text.slice(20)}`;
}
